import { Component, input, model, output } from '@angular/core';

@Component({
  selector: 'app-confirm-dialog',
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [style]="{ width: width() }"
      [closable]="true"
      (visibleChange)="onCancel()"
      [appendTo]="'body'"
    >
      <ng-template #header>
        <div class="cd-header">
          <div class="cd-header__icon" [class]="'cd-header__icon--' + severity()">
            <i [class]="icon()"></i>
          </div>
          <span class="cd-header__title">{{ title() }}</span>
        </div>
      </ng-template>

      <div class="cd-body">
        <p class="cd-body__message">{{ message() }}</p>
        @if (details()) {
          <p class="cd-body__details">{{ details() }}</p>
        }
      </div>

      <ng-template #footer>
        <div class="cd-footer">
          <button class="cd-footer__btn cd-footer__btn--cancel" (click)="onCancel()">
            {{ cancelLabel() }}
          </button>
          <!-- Optional third action. Rendered only when altLabel is supplied, so
               two-button callers (including the idle prompt) are unaffected. -->
          <button
            *ngIf="altLabel()"
            class="cd-footer__btn cd-footer__btn--alt"
            (click)="onAlt()"
          >
            <i class="pi pi-sign-out"></i>
            {{ altLabel() }}
          </button>
          <button class="cd-footer__btn cd-footer__btn--confirm" [class]="'cd-footer__btn cd-footer__btn--confirm cd-footer__btn--' + severity()" (click)="onConfirm()">
            <i [class]="confirmIcon()"></i>
            {{ confirmLabel() }}
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .cd-header {
      display: flex; align-items: center; gap: 0.65rem;
    }
    .cd-header__icon {
      width: 36px; height: 36px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      font-size: 1rem; flex-shrink: 0;
    }
    .cd-header__icon--danger { background: #fee2e2; color: #dc2626; }
    .cd-header__icon--warning { background: #fef3c7; color: #d97706; }
    .cd-header__icon--info { background: #e0f2fe; color: #0369a1; }
    .cd-header__icon--success { background: #dcfce7; color: #16a34a; }
    .cd-header__title {
      font-family: 'Reddit Sans', sans-serif; font-weight: 800;
      font-size: 11pt; color: #041730;
    }
    .cd-body {
      padding: 0.5rem 0;
    }
    .cd-body__message {
      font-family: 'Reddit Sans', sans-serif; font-size: 9.5pt;
      color: #334155; line-height: 1.55; margin: 0;
    }
    .cd-body__details {
      font-family: 'Reddit Sans', sans-serif; font-size: 8.5pt;
      color: #94a3b8; margin: 0.5rem 0 0; font-style: italic;
    }
    .cd-footer {
      display: flex; gap: 0.5rem; justify-content: flex-end;
    }
    .cd-footer__btn {
      font-family: 'Reddit Sans', sans-serif; font-weight: 700;
      font-size: 9pt; padding: 0.5rem 1.1rem; border-radius: 8px;
      border: none; cursor: pointer; transition: all 0.2s ease;
      display: inline-flex; align-items: center; gap: 0.35rem;
      i { font-size: 0.7rem; }
    }
    .cd-footer__btn--cancel {
      background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0;
      &:hover { background: #e2e8f0; }
    }
    // Destructive but deliberately less prominent than the primary action.
    .cd-footer__btn--alt {
      background: #fff; color: #dc2626; border: 1px solid #fca5a5;
      &:hover { background: #fef2f2; border-color: #f87171; }
    }
    .cd-footer__btn--danger {
      background: #dc2626; color: #fff;
      &:hover { background: #b91c1c; box-shadow: 0 2px 8px rgba(220,38,38,0.3); }
    }
    .cd-footer__btn--warning {
      background: #d97706; color: #fff;
      &:hover { background: #b45309; box-shadow: 0 2px 8px rgba(217,119,6,0.3); }
    }
    .cd-footer__btn--info {
      background: #1D4370; color: #fff;
      &:hover { background: #163a5c; box-shadow: 0 2px 8px rgba(29,67,112,0.3); }
    }
    .cd-footer__btn--success {
      background: #16a34a; color: #fff;
      &:hover { background: #15803d; box-shadow: 0 2px 8px rgba(22,163,74,0.3); }
    }
  `],
  standalone: false,
})
export class ConfirmDialogComponent {
  visible = model<boolean>(false);
  title = input<string>('Confirm Action');
  message = input<string>('Are you sure you want to proceed?');
  details = input<string>('');
  confirmLabel = input<string>('Confirm');
  cancelLabel = input<string>('Cancel');
  /**
   * Optional third button, sitting between cancel and confirm. Left empty by
   * default so a plain confirm/cancel dialog renders exactly two buttons.
   */
  altLabel = input<string>('');
  severity = input<'danger' | 'warning' | 'info' | 'success'>('danger');
  icon = input<string>('pi pi-exclamation-triangle');
  confirmIcon = input<string>('pi pi-check');
  width = input<string>('440px');

  confirm = output<void>();
  cancel = output<void>();
  alt = output<void>();

  onConfirm(): void {
    this.confirm.emit();
    this.visible.set(false);
  }

  onCancel(): void {
    this.cancel.emit();
    this.visible.set(false);
  }

  onAlt(): void {
    this.alt.emit();
    this.visible.set(false);
  }
}
